import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

const REASONS = ["Inappropriate Content", "Nudity/Sexual Content", "Offensive Content", "Contact Information", "Low Quality", "Misleading Image", "Duplicate Image", "Suspected Impersonation", "Other"];

export default function AdminModeration() {
  const [tab, setTab] = useState("photos");
  const [summary, setSummary] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [pStatus, setPStatus] = useState("pending");

  const loadSummary = () => api.get("/admin/moderation/summary").then(({ data }) => setSummary(data));
  const loadPhotos = () => api.get("/admin/moderation/photos?status=pending").then(({ data }) => setPhotos(data.photos));
  const loadProfiles = (s = pStatus) => api.get(`/admin/moderation/profiles?status=${s}`).then(({ data }) => setProfiles(data.profiles));

  useEffect(() => { loadSummary(); loadPhotos(); loadProfiles(); /* eslint-disable-next-line */ }, []);

  const decidePhoto = async (owner, pid, decision) => {
    let reason = "";
    if (decision !== "approve") { reason = window.prompt(`Reason (${REASONS.join(", ")})`, "Other") || ""; if (!reason) return; }
    try { await api.post(`/admin/moderation/photos/${owner}/${pid}`, { decision, reason }); toast.success("Done"); loadPhotos(); loadSummary(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const decideProfile = async (uid, decision) => {
    let reason = "";
    if (decision !== "approve") { reason = window.prompt("Reason / message to member", "") || ""; }
    try { await api.post(`/admin/moderation/profiles/${uid}`, { decision, reason }); toast.success("Done"); loadProfiles(); loadSummary(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-moderation">
      <h1 className="font-serif text-3xl font-bold">Moderation Center</h1>
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          {[["Photos pending", summary.photos_pending, "photos"], ["Profiles pending", summary.profiles_pending, "profiles"], ["Safety review", summary.safety_pending, "safety"]].map(([l, v, t]) => (
            <button key={l} onClick={() => t !== "safety" && setTab(t)} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 text-left" data-testid={`mod-summary-${t}`}>
              <div className="font-serif text-2xl font-bold text-[#E2C382]">{v}</div>
              <div className="text-xs text-[#D8B4B9]">{l}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {["photos", "profiles"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`mod-tab-${t}`}
            className={`text-sm px-4 py-2 rounded-full capitalize ${tab === t ? "bg-[#C59B27] text-[#3B0A12]" : "bg-[#2A0F14] text-[#D8B4B9]"}`}>{t} review</button>
        ))}
      </div>

      {tab === "photos" && (!photos ? <FullSpinner /> : (
        photos.length === 0 ? <p className="text-[#D8B4B9] text-sm">No photos pending review.</p> :
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((it) => (
            <div key={it.photo.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl overflow-hidden" data-testid={`mod-photo-${it.photo.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${it.photo.url}?auth=${localStorage.getItem("mj_token")}`} alt="" className="w-full h-40 object-cover bg-[#1A090D]" />
              <div className="p-3 space-y-2">
                <div className="text-xs text-[#D8B4B9]">{it.full_name} <span className="font-mono">{it.profile_code}</span>{it.photo.private ? " · private" : ""}</div>
                <div className="flex gap-1.5">
                  <button onClick={() => decidePhoto(it.user_id, it.photo.id, "approve")} data-testid={`mod-photo-approve-${it.photo.id}`} className="btn-gold text-xs px-2 py-1 rounded flex-1">Approve</button>
                  <button onClick={() => decidePhoto(it.user_id, it.photo.id, "reject")} data-testid={`mod-photo-reject-${it.photo.id}`} className="text-xs px-2 py-1 rounded border border-rose-500/50 text-rose-300 flex-1">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {tab === "profiles" && (
        <>
          <div className="flex gap-2">
            {["pending", "changes_required", "approved", "rejected"].map((s) => (
              <button key={s} onClick={() => { setPStatus(s); loadProfiles(s); }} data-testid={`mod-pstatus-${s}`}
                className={`text-xs px-3 py-1.5 rounded-full ${pStatus === s ? "bg-[#C59B27] text-[#3B0A12]" : "bg-[#2A0F14] text-[#D8B4B9]"}`}>{s.replace(/_/g, " ")}</button>
            ))}
          </div>
          {!profiles ? <FullSpinner /> : profiles.length === 0 ? <p className="text-[#D8B4B9] text-sm">No profiles in this queue.</p> :
            profiles.map((p) => (
              <div key={p.user_id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid={`mod-profile-${p.user_id}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-medium">{p.full_name} <span className="font-mono text-xs text-[#8A6D71]">{p.profile_code}</span></div>
                    <div className="text-xs text-[#D8B4B9]">{p.city || "—"} · {p.age || "—"} · {p.completion}% complete</div></div>
                  <a href={`/admin/user/${p.user_id}`} className="text-xs text-[#C59B27]">Open 360 →</a>
                </div>
                {p.about_me && <p className="text-xs text-[#8A6D71] mt-2 bg-[#1A090D] rounded-lg p-2 line-clamp-3">{p.about_me}</p>}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => decideProfile(p.user_id, "approve")} data-testid={`mod-profile-approve-${p.user_id}`} className="btn-gold text-xs px-3 py-1.5 rounded-lg">Approve</button>
                  <button onClick={() => decideProfile(p.user_id, "changes")} className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-300">Request Changes</button>
                  <button onClick={() => decideProfile(p.user_id, "reject")} className="text-xs px-3 py-1.5 rounded-lg border border-rose-500/50 text-rose-300">Reject</button>
                  <button onClick={() => decideProfile(p.user_id, "escalate")} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Escalate</button>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
}
